import __var_0__ from '@stdlib/assert/is-plain-object';
import __var_1__ from '@stdlib/assert/has-own-property';
import __var_2__ from '@stdlib/assert/is-number';
import __var_3__ from '@stdlib/assert/is-integer';
import __var_4__ from '@stdlib/assert/is-positive-integer';
import __var_5__ from '@stdlib/assert/is-nonnegative-integer';
import __var_6__ from '@stdlib/math/base/assert/is-nan';
/**

*
* Copyright (c) 2019 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var isPlainObject = __var_0__;
var hasOwnProp = __var_1__;
var isNumber = __var_2__.isPrimitive;
var isInteger = __var_3__.isPrimitive;
var isPositiveInteger = __var_4__.isPrimitive;
var isNonNegativeInteger = __var_5__.isPrimitive;
var isnan = __var_6__;


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination object
* @param {Options} options - function options
* @param {PositiveInteger} [options.period] - number of iterations before a waveform repeats
* @param {PositiveInteger} [options.duration] - number of consecutive iterations of maximum amplitude during one period
* @param {number} [options.min] - minimum amplitude
* @param {number} [options.max] - maximum amplitude
* @param {integer} [options.offset] - phase offset (in units of iterations)
* @param {NonNegativeInteger} [options.iter] - number of iterations
* @returns {(Error|null)} null or an error object
*
* @example
* var opts = {};
* var options = {
*     'iter': 100
* };
* var err = validate( opts, options );
* if ( err ) {
*     throw err;
* }
*/
function validate( opts, options ) {
	if ( !isPlainObject( options ) ) {
		return new TypeError( 'invalid argument. Options argument must be an object. Value: `' + options + '`.' );
	}
	if ( hasOwnProp( options, 'duration' ) ) {
		opts.duration = options.duration;
		if ( !isPositiveInteger( options.duration ) ) {
			return new TypeError( 'invalid option. `duration` option must be a positive integer. Option: `' + options.duration + '`.' );
		}
	}
	if ( hasOwnProp( options, 'period' ) ) {
		opts.period = options.period;
		if ( !isPositiveInteger( options.period ) ) {
			return new TypeError( 'invalid option. `period` option must be a positive integer. Option: `' + options.period + '`.' );
		}
	}
	if ( hasOwnProp( options, 'min' ) ) {
		opts.min = options.min;
		if ( !isNumber( options.min ) || isnan( options.min ) ) {
			return new TypeError( 'invalid option. `min` option must be a number. Option: `' + options.min + '`.' );
		}
	}
	if ( hasOwnProp( options, 'max' ) ) {
		opts.max = options.max;
		if ( !isNumber( options.max ) || isnan( options.max ) ) {
			return new TypeError( 'invalid option. `max` option must be a number. Option: `' + options.max + '`.' );
		}
	}
	if ( hasOwnProp( options, 'offset' ) ) {
		opts.offset = options.offset;
		if ( !isInteger( options.offset ) ) {
			return new TypeError( 'invalid option. `offset` option must be an integer. Option: `' + options.offset + '`.' );
		}
	}
	if ( hasOwnProp( options, 'iter' ) ) {
		opts.iter = options.iter;
		if ( !isNonNegativeInteger( options.iter ) ) {
			return new TypeError( 'invalid option. `iter` option must be a nonnegative integer. Option: `' + options.iter + '`.' );
		}
	}
	return null;
}


// EXPORTS //
export default validate;
